import { api } from "../api/Middleware";
import {
  SUBACCOUNTS_URL,
  DELETE_SUBACCOUNT_URL,
  SUBACCOUNT_FEATURES_URL,
  SUBACCOUNT_UPDATE_FEATURES_URL,
} from "../api/Urls";

const SubaccountService = {
  // ========================================
  // MÉTODOS PARA API /api/subaccounts/
  // ========================================

  /**
   * Lista clientes que o usuário pode gerenciar
   * GET /api/subaccounts/
   */
  getSubaccounts: async (params = {}) => {
    try {
      const response = await api.get(SUBACCOUNTS_URL, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccounts:", error);
      throw error;
    }
  },

  /**
   * Obter detalhes de uma subconta específica
   * GET /api/subaccounts/{id}/
   */
  getSubaccountDetails: async (subaccountId) => {
    try {
      const response = await api.get(`${SUBACCOUNTS_URL}${subaccountId}/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccount details:", error);
      throw error;
    }
  },

  /**
   * Alternar status da subconta (ativa/inativa)
   * POST /api/subaccounts/{id}/toggle_status/
   */
  toggleSubaccountStatus: async (subaccountId) => {
    try {
      const response = await api.post(`${SUBACCOUNTS_URL}${subaccountId}/toggle_status/`);
      return response.data;
    } catch (error) {
      console.error("Error toggling subaccount status:", error);
      throw error;
    }
  },

  /**
   * Atualizar limite de usuários da subconta
   * POST /api/subaccounts/{id}/update_user_limit/
   */
  updateUserLimit: async (subaccountId, maxUsersAllowed) => {
    try {
      const response = await api.post(`${SUBACCOUNTS_URL}${subaccountId}/update_user_limit/`, {
        max_users_allowed: maxUsersAllowed
      });
      return response.data;
    } catch (error) {
      console.error("Error updating user limit:", error);
      throw error;
    }
  },

  /**
   * Obter resumo das clientes gerenciadas
   * GET /api/subaccounts/summary/
   */
  getSubaccountsSummary: async () => {
    try {
      const response = await api.get(`${SUBACCOUNTS_URL}summary/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccounts summary:", error);
      throw error;
    }
  },

  /**
   * Deletar subconta permanentemente
   * DELETE /api/account/{id}/delete-subaccount/
   */
  deleteSubaccount: async (subaccountId) => {
    try {
      const response = await api.delete(DELETE_SUBACCOUNT_URL(subaccountId));
      return response.data;
    } catch (error) {
      console.error("Error deleting subaccount:", error);
      throw error;
    }
  },

  // ========================================
  // MÉTODOS PARA FEATURES DE SUBCONTAS
  // ========================================

  /**
   * Listar features de uma subconta
   * GET /api/v1/account/{subaccount_id}/features/
   */
  getSubaccountFeatures: async (subaccountId) => {
    try {
      const response = await api.get(SUBACCOUNT_FEATURES_URL(subaccountId));
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccount features:", error);
      throw error;
    }
  },

  /**
   * Atualizar features de uma subconta (apenas is_enabled)
   * PATCH /api/v1/account/{subaccount_id}/update-features/
   */
  updateSubaccountFeatures: async (subaccountId, features) => {
    try {
      const response = await api.patch(SUBACCOUNT_UPDATE_FEATURES_URL(subaccountId), {
        features: features.map(f => ({
          id: f.id,
          is_enabled: f.is_enabled,
        })),
      });
      return response.data;
    } catch (error) {
      console.error("Error updating subaccount features:", error);
      throw error;
    }
  },

  // ========================================
  // MÉTODOS UTILITÁRIOS
  // ========================================

  /**
   * Validar dados de limite de usuários
   */
  validateUserLimit: (currentUsers, newLimit, organizationMaxLimit) => {
    const errors = [];

    if (!newLimit || newLimit < 1) {
      errors.push("Limite deve ser maior que 0");
    }

    if (newLimit < currentUsers) {
      errors.push(`Limite não pode ser menor que usuários atuais (${currentUsers})`);
    }

    if (organizationMaxLimit && newLimit > organizationMaxLimit) {
      errors.push(`Limite não pode exceder o máximo da organização (${organizationMaxLimit})`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Formatar dados da subconta para exibição
   */
  formatSubaccountData: (subaccount) => {
    return {
      ...subaccount,
      usagePercentage: subaccount.max_users_allowed > 0 
        ? ((subaccount.current_users_count || 0) / subaccount.max_users_allowed) * 100 
        : 0,
      usersRemaining: subaccount.max_users_allowed - (subaccount.current_users_count || 0),
      statusText: subaccount.subaccount_active ? "Ativa" : "Inativa",
      statusVariant: subaccount.subaccount_active ? "success" : "secondary",
      formattedCreatedAt: new Date(subaccount.created_at).toLocaleDateString("pt-BR"),
      relativeCreatedAt: subaccount.created_at
    };
  },

  /**
   * Verificar se pode executar ação na subconta
   */
  canPerformAction: (action, userContext, subaccount) => {
    const { isOrganization, user } = userContext;

    // Verificar se é organização
    if (!isOrganization()) {
      return false;
    }

    // Verificar se é dono da organização
    const isOwner = user?.is_superuser && user?.superuser_level === 1;

    switch (action) {
      case "view":
        // Gerentes e acima podem ver
        return user?.superuser_level <= 2;
      
      case "edit":
        // Apenas donos podem editar
        return isOwner;
      
      case "toggle_status":
        // Apenas donos podem ativar/desativar
        return isOwner;
      
      case "update_user_limit":
        // Apenas donos podem alterar limites
        return isOwner;

      case "delete":
        // Apenas donos podem deletar subcontas
        return isOwner;

      default:
        return false;
    }
  },

  /**
   * Gerar filtros para busca
   */
  buildSearchParams: (filters) => {
    const params = {};

    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }

    if (filters.status && filters.status !== "all") {
      params.status = filters.status;
    }

    if (filters.page) {
      params.page = filters.page;
    }

    if (filters.page_size) {
      params.page_size = filters.page_size;
    }

    return params;
  },

  /**
   * Calcular estatísticas das clientes
   */
  calculateStats: (subaccounts) => {
    const stats = {
      total: subaccounts.length,
      active: 0,
      inactive: 0,
      totalUsers: 0,
      totalUserLimit: 0
    };

    subaccounts.forEach(subaccount => {
      if (subaccount.subaccount_active) {
        stats.active++;
      } else {
        stats.inactive++;
      }

      stats.totalUsers += subaccount.current_users_count || 0;
      stats.totalUserLimit += subaccount.max_users_allowed || 0;
    });

    stats.utilizationPercentage = stats.totalUserLimit > 0 
      ? (stats.totalUsers / stats.totalUserLimit) * 100 
      : 0;

    return stats;
  }
};

export default SubaccountService;